import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { RetriggerGroupSyncService } from '../../services/retrigger-group-sync.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  gridPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-retrigger-group-sync',
    templateUrl: './retrigger-group-sync.component.html',
    styleUrls: ['./retrigger-group-sync.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class RetriggerGroupSyncComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // Standard component properties
  modalTitle = 'Retrigger Group Sync';
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;

  // Component-specific properties
  groups: string[] = [];
  taxYear: string = '';
  clientKey: string = '';

  // Global settings
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};
  gridData: any[] = [];

  private readonly subs = new Subscription();

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private retriggerGroupSyncService: RetriggerGroupSyncService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.USER_SETTINGS = header.userSettingsData || {};
    const g = gridPayloadFromActiveScreen(activeScreen);
    this.gridData = Array.isArray(g) ? g : g != null ? [g] : [];
    this.cdr.markForCheck();
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  init() {
    try {
      // Extract groups and tax year from gridData
      this.groups = [];
      this.gridData.forEach((item: any) => {
        if (item.data && item.data.GROUP_NAME) {
          this.groups.push(item.data.GROUP_NAME);
        }
        if (item.data && item.data.TAX_YEAR) {
          this.taxYear = item.data.TAX_YEAR;
        }
      });

      // Get client key from USER_SETTINGS
      if (this.USER_SETTINGS && this.USER_SETTINGS.user) {
        this.clientKey = this.USER_SETTINGS.user.client_key;
      }

      this.cdr.detectChanges();
    } catch (e) {
      console.error('Error in RetriggerGroupSync init:', e);
    }
  }

  removeTag(index: number) {
    this.groups.splice(index, 1);
    this.cdr.detectChanges();
  }

  retriggerSyncGroup() {
    if (this.groups.length === 0) {
      this._snackBar.open('No groups selected', 'Close', {
        duration: 4000,
        panelClass: ['warning-snackbar']
      });
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;

    const groupsString = this.groups.join(',');

    this.retriggerGroupSyncService.retriggerSync(
      this.GENERAL_CONFIG.base_url,
      this.taxYear,
      groupsString
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.callSuccess == "1") {
          this._snackBar.open('Re-sync Group Execute Successfully', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        } else {
          this._snackBar.open(response.message || 'Error occurred while retriggering sync', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        }

        this.cdr.detectChanges();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        console.error('Error retriggering sync:', error);
        this._snackBar.open('Error occurred while retriggering sync. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}
